<template>
<div>
    <!-- Hero Section -->
    <section class="pt-12">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-5 col-lg-6 order-md-2">
                    <img src="assets/img/illustrations/illustration-4.png" alt="Instagram datasets download" class="img-fluid mb-6 mb-md-0">
                </div>
                <div class="col-12 col-md-7 col-lg-6 order-md-1">
                    <h1 class="display-4">
                        Instagram Datasets
                        <span class="text-primary d-block fs-2">Download User Profiles, Posts & Comments</span>
                    </h1>
                    <p class="fs-lg text-gray-700 mb-6">
                        Download ready-made <strong>Instagram datasets</strong> with millions of user profiles, posts, comments, and engagement metrics. CSV format, instant download. Perfect for <strong>data science</strong>, <strong>machine learning</strong>, market research, and social media analytics.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- Products Section -->
    <section class="py-4 py-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12">
                    <h2 class="mb-4 text-center">Available Datasets</h2>
                    <div v-if="!loading && countries.length" class="d-flex flex-wrap justify-content-center gap-2 mb-5">
                        <button
                            type="button"
                            class="country-pill"
                            :class="{ active: !selectedCountry }"
                            @click="selectedCountry = null"
                        >
                            🌐 All countries
                        </button>
                        <button
                            v-for="code in countries"
                            :key="code"
                            type="button"
                            class="country-pill"
                            :class="{ active: selectedCountry === code }"
                            @click="selectedCountry = selectedCountry === code ? null : code"
                        >
                            {{ countryFlag(code) }} {{ countryName(code) }}
                        </button>
                    </div>
                    <Spinner v-if="loading" />
                    <ProductsTable
                        v-else
                        :products="displayedProducts"
                        :buyable="true"
                        @buy="openBuyModal"
                    />
                    <p v-if="!loading && availableProducts.length === 0" class="text-center text-muted">
                        No datasets available at the moment.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- My Purchases Section -->
    <section v-if="$state.context.id" id="my-datasets" class="py-4 py-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12">
                    <h2 class="mb-4 text-center">My Datasets</h2>
                    <Spinner v-if="purchasesLoading" />
                    <div v-else-if="purchases.length > 0" class="table-responsive">
                        <table class="table table-hover table-dark">
                            <tbody>
                                <tr v-for="purchase in purchases" :key="purchase.id">
                                    <td>{{ purchase.title }}</td>
                                    <td>
                                        <span :class="statusBadgeClass(purchase.status)">
                                            {{ purchase.status_display }}
                                        </span>
                                    </td>
                                    <td>{{ purchase.row_count.toLocaleString() }}</td>
                                    <td>{{ formatDate(purchase.created_at) }}</td>
                                    <td class="text-end">
                                        <a
                                            v-if="purchase.download_url"
                                            :href="purchase.download_url"
                                            class="btn btn-sm btn-success"
                                            download
                                        >
                                            Download
                                        </a>
                                        <span v-else-if="purchase.status === 'P' || purchase.status === 'R'" class="text-muted">
                                            Generating...
                                        </span>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="text-center text-muted">
                        You haven't purchased any datasets yet.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- Dataset Builder Section -->
    <!-- TODO(2026-06-25): hidden until DatasetBuilder is finished (see 2026-06-25-dataset-builder-status.md) -->
    <section class="py-4 py-md-6" v-if="false">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-4">Need Custom Data?</h2>
                    <p class="fs-lg text-muted mb-4">
                        Can't find what you need in our ready-made datasets? Use <strong>Dataset Builder</strong> to collect custom data by hashtags, locations, or usernames.
                    </p>
                    <router-link v-if="$state.context.id" class="btn btn-primary" to="/datasets/builder">
                        Open Dataset Builder
                    </router-link>
                    <router-link v-else class="btn btn-outline-primary" to="/registration">
                        Register to use Dataset Builder
                    </router-link>
                </div>
            </div>
        </div>
    </section>

    <!-- Use Cases Section -->
    <section class="py-4 py-md-6 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">
                    <h2 class="mb-4">What Can You Do With Instagram Datasets?</h2>
                    <ul class="fs-lg text-muted">
                        <li class="mb-2"><strong>Market research</strong> — analyze competitor Instagram accounts and audience demographics</li>
                        <li class="mb-2"><strong>Influencer marketing</strong> — find and evaluate Instagram influencers for campaigns</li>
                        <li class="mb-2"><strong>Academic research</strong> — study social media trends and user behavior patterns</li>
                        <li class="mb-2"><strong>Machine learning</strong> — train models on Instagram user data and engagement metrics</li>
                        <li class="mb-2"><strong>Sentiment analysis</strong> — analyze Instagram comments for brand monitoring</li>
                        <li class="mb-2">Lead generation for B2B sales</li>
                    </ul>
                </div>
            </div>
        </div>
    </section>

    <!-- CTA Section -->
    <section class="py-2 py-md-6">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 v-show="!$state.context.id">
                        <router-link class="fw-bold text-primary-desat" :to="{path: '/registration'}">
                            Register
                        </router-link> and start using our service!
                    </h2>
                    <p class="fs-lg text-muted mb-7 mb-md-9">
                        Top up your account in your personal dashboard, get token and use any plan
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- Buy Modal -->
    <div class="modal fade" id="buyModal" tabindex="-1" aria-hidden="true" ref="buyModal">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content bg-dark text-light">
                <div class="modal-header border-secondary">
                    <h5 class="modal-title">Purchase Dataset</h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body py-3" v-if="selectedItem">
                    <div class="mb-3">
                        <h6 class="mb-1">{{ selectedProduct.title }}</h6>
                        <p class="text-muted mb-1">{{ selectedItem.name }} - {{ selectedItem.row_limit.toLocaleString() }} rows</p>
                        <h5 class="text-primary mb-0">${{ selectedItem.price }}</h5>
                    </div>

                    <div v-if="$state.context.id">
                        <div class="mb-2 d-flex align-items-center" v-if="userBalance > 0">
                            <div class="form-check">
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    id="useBalanceCheck"
                                    v-model="useBalanceEnabled"
                                >
                                <label class="form-check-label" for="useBalanceCheck">
                                    Use from balance <strong>${{ maxBalanceUse.toFixed(2) }}</strong>
                                    <small class="text-muted">(available: ${{ userBalance.toFixed(2) }})</small>
                                </label>
                            </div>
                        </div>
                        <div class="alert alert-dark border-secondary py-2 mb-2">
                            <div class="d-flex justify-content-between">
                                <span>Amount to pay:</span>
                                <strong>${{ amountToPay.toFixed(2) }}</strong>
                            </div>
                            <div v-if="selectedSystem && selectedSystem.currency__code !== 'USD'" class="d-flex justify-content-between mt-2 pt-2 border-top">
                                <span>In {{ selectedSystem.currency__code }}:</span>
                                <strong>{{ selectedSystem.currency__symbol }}{{ amountInCurrency.toFixed(2) }}</strong>
                            </div>
                        </div>

                        <!-- Payment Systems -->
                        <div v-if="amountToPay > 0 && paySystems.length > 0" class="mb-3">
                            <label class="form-label small text-muted">Select payment method:</label>
                            <div class="payment-methods">
                                <label
                                    v-for="system in paySystems"
                                    :key="system.id"
                                    class="payment-method"
                                    :class="{ selected: selectedSystem && selectedSystem.id === system.id }"
                                >
                                    <input
                                        type="radio"
                                        v-model="selectedSystem"
                                        :value="system"
                                        class="d-none"
                                    >
                                    <span class="payment-method-name">
                                        {{ system.description }}
                                        <small class="text-success ms-2" v-if="system.client_fee_percent < 0">
                                            +{{ -system.client_fee_percent }}% bonus
                                        </small>
                                        <small class="text-danger ms-2" v-else-if="system.client_fee_percent > 0">
                                            {{ system.client_fee_percent }}% fee
                                        </small>
                                    </span>
                                    <span class="badge bg-secondary">{{ system.currency__code }}</span>
                                </label>
                            </div>
                        </div>

                        <div class="text-danger mb-3" v-if="buyError">{{ buyError }}</div>
                    </div>
                    <div v-else class="alert alert-warning">
                        Please <router-link to="/login" @click="closeBuyModal">login</router-link> or <router-link to="/registration" @click="closeBuyModal">register</router-link> to purchase datasets.
                    </div>
                </div>
                <div class="modal-footer border-secondary py-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button
                        v-if="$state.context.id"
                        type="button"
                        class="btn btn-sm btn-primary"
                        @click="processPurchase"
                        :disabled="buyLoading || (amountToPay > 0 && !selectedSystem)"
                    >
                        <span v-if="buyLoading">Processing...</span>
                        <span v-else-if="amountToPay > 0">Pay ${{ amountToPay.toFixed(2) }}</span>
                        <span v-else>Purchase Now</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import Spinner from "./Spinner"
import ProductsTable from "./ProductsTable"
import { Modal } from "bootstrap"
import { processPayment } from "utils/payment"

export default {
    components: { Spinner, ProductsTable },
    data() {
        return {
            loading: true,
            products: [],
            countriesRef: [],
            selectedCountry: null,
            paySystems: [],
            purchases: [],
            purchasesLoading: false,
            pollInterval: null,
            selectedProduct: null,
            selectedItem: null,
            selectedSystem: null,
            useBalanceEnabled: true,
            buyLoading: false,
            buyError: null,
            modalInstance: null,
        }
    },
    computed: {
        availableProducts() {
            // Filter products that have at least one available item
            return this.products.filter(product => {
                return product.items.some(item => item.row_limit <= product.available_count)
            })
        },
        countries() {
            // Countries reference (server-ordered), narrowed to countries with available products
            const availCodes = new Set(this.availableProducts.map(p => p.country).filter(Boolean))
            return this.countriesRef.filter(c => availCodes.has(c.code)).map(c => c.code)
        },
        countryNames() {
            return Object.fromEntries(this.countriesRef.map(c => [c.code, c.name]))
        },
        displayedProducts() {
            if (!this.selectedCountry) return this.products
            return this.products.filter(p => p.country === this.selectedCountry)
        },
        userBalance() {
            return this.$state.context.amount || 0
        },
        maxBalanceUse() {
            if (!this.selectedItem) return 0
            return Math.min(this.userBalance, this.selectedItem.price)
        },
        useBalance() {
            if (!this.useBalanceEnabled) return 0
            return this.maxBalanceUse
        },
        amountToPay() {
            if (!this.selectedItem) return 0
            return Math.max(0, this.selectedItem.price - this.useBalance)
        },
        amountInCurrency() {
            if (!this.selectedSystem || !this.selectedSystem.currency__rate) return 0
            return this.amountToPay * parseFloat(this.selectedSystem.currency__rate)
        }
    },
    methods: {
        fetchProducts() {
            this.loading = true
            this.$services.Products().get().then(resp => {
                if (resp.body.state) {
                    this.products = resp.body.products
                    this.countriesRef = resp.body.countries || []
                }
            }).finally(() => {
                this.loading = false
            })
        },
        countryFlag(code) {
            return [...code.toUpperCase()].map(ch => String.fromCodePoint(0x1F1E6 + ch.charCodeAt(0) - 65)).join('')
        },
        countryName(code) {
            return this.countryNames[code] || code
        },
        fetchPaySystems() {
            this.$services.PaymentSystem().get().then(resp => {
                // Filter out crypto (cc) systems for simplicity in popup
                this.paySystems = resp.body.data.filter(s => s.group !== 'cc')
            })
        },
        fetchPurchases() {
            if (!this.$state.context.id) return
            this.purchasesLoading = true
            this.$services.Purchases().get().then(resp => {
                if (resp.body.state) {
                    this.purchases = resp.body.purchases
                }
            }).finally(() => {
                this.purchasesLoading = false
            })
        },
        statusBadgeClass(status) {
            const classes = {
                'P': 'badge bg-warning',
                'R': 'badge bg-info',
                'C': 'badge bg-success',
                'F': 'badge bg-danger'
            }
            return classes[status] || 'badge bg-secondary'
        },
        formatDate(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleDateString()
        },
        openBuyModal(product, item) {
            this.selectedProduct = product
            this.selectedItem = item
            this.useBalanceEnabled = true
            this.selectedSystem = null
            this.buyError = null

            if (!this.modalInstance) {
                this.modalInstance = new Modal(this.$refs.buyModal)
            }
            this.modalInstance.show()
        },
        closeBuyModal() {
            if (this.modalInstance) {
                this.modalInstance.hide()
            }
        },
        checkBuyQuery() {
            const buyItemId = this.$route.query.buy
            if (!buyItemId || this.products.length === 0) return

            // Find the product and item by item ID
            for (const product of this.products) {
                const item = product.items.find(i => i.id === parseInt(buyItemId))
                if (item) {
                    this.openBuyModal(product, item)
                    // Clear the query parameter
                    this.$router.replace({ path: '/datasets' })
                    break
                }
            }
        },
        hasPendingPurchases() {
            return this.purchases.some(p => p.status === 'P' || p.status === 'R')
        },
        startPolling() {
            if (this.pollInterval) return
            this.pollInterval = setInterval(() => {
                if (this.hasPendingPurchases()) {
                    this.fetchPurchases()
                } else {
                    this.stopPolling()
                }
            }, 5000)
        },
        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval)
                this.pollInterval = null
            }
        },
        scrollToMyDatasets() {
            this.$nextTick(() => {
                const section = document.getElementById('my-datasets')
                if (section) section.scrollIntoView({ behavior: 'smooth' })
            })
        },
        processPurchase() {
            if (!this.$state.context.id) {
                this.$router.push('/login')
                return
            }

            this.buyLoading = true
            this.buyError = null

            if (this.amountToPay <= 0) {
                // Purchase with balance only
                this.$services.ProductBuy().save({
                    item_id: this.selectedItem.id,
                    use_balance: this.useBalance
                }).then(resp => {
                    if (resp.body.state) {
                        this.closeBuyModal()
                        this.$services.Context().get().then(r => {
                            this.$state.context = r.body
                        })
                        this.fetchPurchases()
                        this.scrollToMyDatasets()
                    } else {
                        this.buyError = resp.body.message || 'Purchase failed. Please try again.'
                    }
                }).catch(err => {
                    this.buyError = 'An error occurred. Please try again.'
                }).finally(() => {
                    this.buyLoading = false
                })
            } else {
                // Need to pay - use payment system
                if (!this.selectedSystem) {
                    this.buyError = 'Please select a payment method.'
                    this.buyLoading = false
                    return
                }

                this.$services.Payment().save({
                    amount: this.amountInCurrency || this.amountToPay,
                    system: this.selectedSystem.id,
                    product_item_id: this.selectedItem.id,
                    balance_used: this.useBalance
                }).then(resp => {
                    processPayment(
                        this,
                        resp,
                        null,
                        (errors) => {
                            this.buyError = errors.amount ? errors.amount[0] : 'Payment error. Please try again.'
                        }
                    )
                }).catch(err => {
                    this.buyError = 'An error occurred. Please try again.'
                }).finally(() => {
                    this.buyLoading = false
                })
            }
        }
    },
    created() {
        this.fetchProducts()
        this.fetchPaySystems()
        // fetchPurchases is called by watch on $state.context.id with immediate: true
    },
    mounted() {
        // Scroll to My Datasets if hash is present
        if (this.$route.hash === '#my-datasets') {
            this.scrollToMyDatasets()
        }
    },
    watch: {
        products() {
            // Check if there's a buy query parameter to auto-open modal
            this.checkBuyQuery()
            // Drop stale country selection after refetch
            if (this.selectedCountry && !this.countries.includes(this.selectedCountry)) {
                this.selectedCountry = null
            }
        },
        purchases() {
            if (this.hasPendingPurchases()) {
                this.startPolling()
            } else {
                this.stopPolling()
            }
        },
        '$state.context.id': {
            handler(newVal) {
                // Fetch purchases when user logs in or context loads
                if (newVal) {
                    this.fetchPurchases()
                }
            },
            immediate: true
        }
    },
    beforeUnmount() {
        this.stopPolling()
    }
}
</script>
<style scoped>
.country-pill {
    padding: 0.35rem 0.9rem;
    font-size: 0.9rem;
    border-radius: 2rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: inherit;
    transition: all 0.15s ease;
}
.country-pill:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
}
.country-pill.active {
    background: rgba(0, 212, 170, 0.15);
    border-color: #00d4aa;
    color: #00d4aa;
}
.payment-methods {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.payment-method {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.15s ease;
}
.payment-method:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
}
.payment-method.selected {
    background: rgba(0, 212, 170, 0.15);
    border-color: #00d4aa;
}
.payment-method-name {
    font-size: 0.9rem;
}
</style>
